import * as React from "react";

import { constantSearchParams, constantTable } from "../../helpers/constants";
import { DataTableBase, DataTableBaseProps } from './DataTableBase';
import { PagedList } from "../../types/dataQueries/base/PagedList";
import { useURLSearchParam } from "../../helpers/hooks/useURLSearchParam";
import { DataQueryBase } from "../../types/dataQueries/base/DataQueryBase";


export type DataTablePagedListProps<T> = DataTableBaseProps & {
    data: PagedList<T>;
    updateQuery: (query: DataQueryBase<T>) => void;
    externalFilters?: { filters?: any; multiSortMeta?: any };
  };

export interface TableState extends IPage{
  multiSortMeta: any; // ToDo: add type
  filters?: any;
}
export interface IMultiSortMeta{
  field: string;
  order: number;
}
export interface IPage{
  first: number;
  rows: number;

  page?: number;
  pageCount?: number;
}

export const DataTablePagedList: <T>(props: DataTablePagedListProps<T>) => React.ReactElement = <T,>(rawProps: DataTablePagedListProps<T>) => {
  const { externalFilters, data, updateQuery, ...props } = rawProps;
  const { searchParams, onUpdate } = useURLSearchParam();
  const [tableState, setTableState] = React.useState<TableState>({ first: 0, rows: constantTable.paginatorRows, multiSortMeta: [] })
  const storage = React.useMemo(() => sessionStorage, []);


  const updateFiltersCallback = React.useCallback((state: TableState = tableState) => {
    let f = {};
    if (state.filters) {
      props.allColumns
        .filter((x) => x.filter !== false || x.filterField)
        .forEach(x => {
          if (x.field || x.filterField) {
            const key = x.filterField ?? x.field ?? ``;
            f[key] = Object.keys(state.filters).indexOf(key) === -1 ? `` : state.filters[key].value;
          }
        });
    }

    updateQuery({
      ...f,
      Skip: state.first,
      Take: state.rows
    });
  }, [props.allColumns, tableState, updateQuery]);


  const onPage = React.useCallback((e: IPage) => {
    setTableState({ ...tableState, ...e })
    onUpdate(constantSearchParams.PageOffset, "" + e.first)

    updateQuery({
      Skip: e.first,
      Take: e.rows,
    });
  }, [onUpdate, tableState, updateQuery]);

  const onSort = React.useCallback((e) => {
    setTableState({ ...tableState, ...e })

    updateQuery({
      Sorts: e.multiSortMeta.map((element) => {
        return { Field: element.field, Desc: element.order === 1 };
      }),
    });
  }, [tableState, updateQuery]);

  const onFilter = React.useCallback((e) => {
    const state: TableState = { ...tableState, ...e };
    state.filters = { ...state.filters, ...e.filters };
    setTableState(state);
    updateFiltersCallback(state);
  }, [tableState, updateFiltersCallback]);



  const customRestoreState = React.useCallback(() => {
    const initialState = storage.getItem(props.tableName);
    const stored = initialState ? JSON.parse(initialState) : {};
    const pageOffset = searchParams.get(constantSearchParams.PageOffset) ?? 0;
    const state: TableState = { ...tableState, ...stored, ...(externalFilters ?? {}), first: +pageOffset };
    setTableState(state);
    onUpdate(constantSearchParams.PageOffset, "" + (+pageOffset));
    updateFiltersCallback(state);
    return {};
    // eslint-disable-next-line
  }, [props.tableName, externalFilters, storage, onPage, searchParams]);

  const customSaveState = React.useCallback((state: TableState) => {
    if (Object.keys(state).length) {
      storage.setItem(props.tableName, JSON.stringify(state))
    }
  }, [props.tableName, storage]);

  
  return (
    <DataTableBase
      emptyMessage={constantTable.emptyMessage}
      //storage
      stateKey={props.tableName}
      stateStorage="custom"
      customSaveState={customSaveState}
      customRestoreState={customRestoreState}
      //lazy mode
      lazy
      value={data.Items}
      totalRecords={data.TotalCount}
      //resize
      resizableColumns
      columnResizeMode="fit"
      //sort
      removableSort
      sortMode="multiple"
      multiSortMeta={tableState.multiSortMeta}
      onSort={onSort}
      //filter
      filters={tableState.filters}
      onFilter={onFilter}
      //pagination
      paginator
      onPage={onPage}
      first={tableState.first}
      rows={tableState.rows}
      {...props} />
  );
};